import { ArrowDown } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';

import { RecordingDetailsBox } from './RecordingDetailsBox';
import { SpeakersPanel } from './SpeakersPanel';
import { TranscriptBlocks } from './TranscriptBlocks';
import { TranscriptionHeader } from './TranscriptHeader';
import { TranscriptInconsistencies } from './TranscriptInconsistencies';
import { RecordingLoadingSpinner, RecordingCompletingSpinner } from './TranscriptLoading';
import { LogicalSpeaker, InternalSpeaker } from '../hooks/useTranscription';
import { LiveTranscriptBlock } from '../types/transcriptTypes';
import { TranscriptInconsistency } from '@/api/types/backend-dto-types';

interface LiveTranscriptionProps {
  transcriptMetadata: {
    otherSpeakers: string[] | null;
    deponent: string | null;
    createdDate: Date | null;
    transcriptName: string | null;
    phraseList: string[] | null;
    objective: string | null;
  };
  // Local state
  displayBlocks: LiveTranscriptBlock[];
  displayLogicalSpeakers: LogicalSpeaker[];
  displayInternalSpeakers: InternalSpeaker[];

  isRecording: boolean;
  isPaused: boolean;
  isConnected: boolean;
  isCompleting: boolean;
  transcriptContainerRef: React.RefObject<HTMLDivElement>;
  isUserScrolling: boolean;
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  scrollToBottom: () => void;
  scrollToTranscriptBlock: (blockId: string) => void;
  handleScrollToBlockFromInconsistency: (blockId: string) => void;
  displayInconsistencies: TranscriptInconsistency[];
  editingSpeaker: string | null;
  editingName: string;
  startEditingSpeaker: (speaker: LogicalSpeaker | InternalSpeaker) => void;
  cancelEditingSpeaker: () => void;
  saveSpeakerMapping: (speakerId: string, name: string) => Promise<void>;
  setEditingName: (name: string) => void;
  updateBlock?: (blockId: string, content?: string, speakerId?: string | null) => void;
  acceptDiarizationCorrection?: (correctionId: string, blockId: string) => void;
  rejectDiarizationCorrection?: (correctionId: string, blockId: string) => void;
  startRecording?: () => void;
  pauseRecording?: () => void;
  completeRecording?: () => void;
  isLoadingMode: boolean;
}

export const LiveTranscription = ({
  transcriptMetadata,
  isRecording,
  isPaused,
  isConnected,
  isCompleting,
  displayBlocks,
  displayLogicalSpeakers,
  displayInternalSpeakers,
  transcriptContainerRef,
  isUserScrolling,
  handleScroll,
  scrollToBottom,
  scrollToTranscriptBlock,
  handleScrollToBlockFromInconsistency,
  displayInconsistencies,
  editingSpeaker,
  editingName,
  startEditingSpeaker,
  cancelEditingSpeaker,
  saveSpeakerMapping,
  setEditingName,
  updateBlock,
  acceptDiarizationCorrection,
  rejectDiarizationCorrection,
  startRecording,
  pauseRecording,
  completeRecording,
  isLoadingMode,
}: LiveTranscriptionProps) => {
  const [searchParams] = useSearchParams();
  const { transcriptId } = useParams();
  const caseId = searchParams.get('caseId');
  return (
    <div className="flex h-full flex-col">
      <TranscriptionHeader
        transcriptName={transcriptMetadata?.transcriptName || ''}
        transcriptCreatedDate={transcriptMetadata?.createdDate || null}
        transcriptDeponent={transcriptMetadata?.deponent || null}
        transcriptObjective={transcriptMetadata?.objective || null}
        transcriptOtherSpeakers={transcriptMetadata?.otherSpeakers || null}
        transcriptPhraseList={transcriptMetadata?.phraseList || null}
        audioControls={
          <RecordingDetailsBox
            isRecording={isRecording}
            isPaused={isPaused}
            isConnected={isConnected}
            caseId={caseId}
            onStartRecording={startRecording || (() => {})}
            onPauseRecording={pauseRecording || (() => {})}
            onCompleteRecording={completeRecording || (() => {})}
          />
        }
      />

      <div className="flex flex-grow flex-col overflow-y-auto">
        {isLoadingMode ? (
          <RecordingLoadingSpinner />
        ) : (
          <>
            <div className="flex flex-1 overflow-hidden bg-white">
              <div className="relative flex w-3/5 max-w-3/5 flex-col overflow-hidden bg-gray-50">
                {displayLogicalSpeakers.length > 0 && (
                  <SpeakersPanel
                    logicalSpeakers={displayLogicalSpeakers}
                    internalSpeakers={displayInternalSpeakers}
                    editingSpeaker={editingSpeaker}
                    editingName={editingName}
                    onStartEditing={startEditingSpeaker}
                    onCancelEditing={cancelEditingSpeaker}
                    onSaveSpeaker={saveSpeakerMapping}
                    onEditingNameChange={setEditingName}
                    deponent={transcriptMetadata?.deponent}
                    otherSpeakers={transcriptMetadata?.otherSpeakers || []}
                  />
                )}

                <div
                  ref={transcriptContainerRef}
                  className="relative flex-1 overflow-y-auto p-4"
                  onScroll={handleScroll}
                >
                  {isCompleting ? (
                    <RecordingCompletingSpinner />
                  ) : displayBlocks.length === 0 ? (
                    <div className="flex h-full items-center justify-center">
                      <p className="text-center text-sm text-gray-500">Start recording to see transcript</p>
                    </div>
                  ) : (
                    <TranscriptBlocks
                      displayBlocks={displayBlocks}
                      caseId={caseId || ''}
                      transcriptId={transcriptId || ''}
                      speakers={displayLogicalSpeakers}
                      displayInconsistencies={displayInconsistencies}
                      currentTime={0}
                      onBlockClick={(block) => scrollToTranscriptBlock(block.id)}
                      onUpdateBlock={updateBlock}
                      onAcceptCorrection={acceptDiarizationCorrection}
                      onRejectCorrection={rejectDiarizationCorrection}
                    />
                  )}
                </div>

                {isUserScrolling && displayBlocks.length > 0 && (
                  <button
                    onClick={scrollToBottom}
                    className="absolute right-4 bottom-4 flex h-10 w-10 items-center justify-center rounded-full bg-slate-600 text-white shadow-lg transition-colors hover:bg-slate-700"
                    aria-label="Scroll to bottom"
                  >
                    <ArrowDown />
                  </button>
                )}
              </div>

              <TranscriptInconsistencies
                caseId={caseId ?? ''}
                displayInconsistencies={displayInconsistencies}
                isPlaybackMode={false}
                handleScrollToBlockFromInconsistency={handleScrollToBlockFromInconsistency}
                numberOfBlocks={displayBlocks.length}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
